/**
 * Main application provider that handles initialization and provides global context.
 * Uses the FastAPI backend exclusively (Firebase migration complete).
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { config, initializeConfig } from '@/lib/config';

// Context for application state
interface AppContextType {
  isInitialized: boolean;
  isInitializing: boolean;
  initializationError: string | null;
  retryInitialization: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initializationError, setInitializationError] = useState<string | null>(null);

  const initialize = async () => {
    try {
      setIsInitializing(true);
      setInitializationError(null);

      // Initialize configuration
      initializeConfig();

      console.log('✅ Hobbes Platform initialized (using API backend)');
      
      setIsInitialized(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown initialization error';
      setInitializationError(errorMessage);
      console.error('App initialization failed:', error);
    } finally {
      setIsInitializing(false);
    }
  };

  const retryInitialization = () => {
    initialize();
  };

  useEffect(() => {
    initialize();
  }, []);

  const contextValue: AppContextType = {
    isInitialized,
    isInitializing,
    initializationError,
    retryInitialization,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

// Loading component for initialization
export const AppInitializationScreen: React.FC = () => {
  const { isInitializing, initializationError, retryInitialization } = useAppContext();

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Initializing Hobbes Platform</h2>
          <p className="text-gray-600">
            Setting up API backend...
          </p>
        </div>
      </div>
    );
  }

  if (initializationError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="rounded-full h-12 w-12 bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 19c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Initialization Failed</h2>
          <p className="text-gray-600 mb-4">{initializationError}</p>
          
          <button
            onClick={retryInitialization}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Retry Initialization
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// Higher-order component to ensure app is initialized
export const withAppInitialization = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { isInitialized } = useAppContext();

    if (!isInitialized) {
      return <AppInitializationScreen />;
    }

    return <Component {...props} />;
  };
};

export default AppProvider;